import { AREAS } from "@/constants/testIds";
import Reveal from "@/components/Reveal";
import { useT } from "@/i18n/LanguageContext";

// Place names — kept untranslated (proper nouns).
const areas = [
  "Liverpool", "Birkenhead", "Bootle", "Crosby", "Kirkby", "Huyton",
  "Prescot", "Wallasey", "St Helens", "Speke", "Aigburth", "Wavertree",
  "Anfield", "Walton", "West Derby", "Allerton", "Childwall", "Garston",
  "Toxteth", "Maghull", "Aintree", "Formby",
];

// Search phrases — always in English for SEO relevance.
const SEO_PHRASES = [
  "Mobile tyre fitter near me",
  "24/7 tyre fitter Liverpool",
  "Emergency puncture repair",
  "Flat tyre help Merseyside",
  "Roadside tyre replacement",
  "Tyre fitter open now",
  "Blowout assistance Liverpool",
  "Same-day tyre fitting",
  "Van tyre fitter",
  "4x4 tyre replacement",
  "Run-flat tyre specialist",
  "Part-worn tyres Liverpool",
  "Motorway tyre help (M53 / M57 / M62)",
  "Driveway tyre fitting",
  "Work-place tyre fitting",
  "Weekend tyre fitter",
  "Night-time tyre fitter",
];

export default function Areas() {
  const { t } = useT();
  const items = [...areas, ...areas];

  return (
    <section
      id="areas"
      data-testid={AREAS.section}
      className="relative bg-[#F5F5F7] py-16 sm:py-24 lg:py-32 overflow-hidden border-y border-black/5"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mb-12 lg:mb-16">
        <Reveal>
          <div className="overline text-[#1E63FF]">{t.areas.overline}</div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mt-4">
            <h2 className="font-display font-semibold text-3xl sm:text-5xl lg:text-6xl tracking-tight text-[#0A0A0A] max-w-2xl leading-[1.02]">
              {t.areas.heading1}<br />
              <span className="text-[#1E63FF]">{t.areas.heading2}</span>
            </h2>
            <p className="text-neutral-600 max-w-md text-base leading-relaxed">
              {t.areas.sub}
            </p>
          </div>
        </Reveal>
      </div>

      {/* Marquee */}
      <div
        data-testid={AREAS.marquee}
        className="relative w-full overflow-hidden no-scrollbar"
      >
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-24 lg:w-48 bg-gradient-to-r from-[#F5F5F7] to-transparent z-10" />
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-24 lg:w-48 bg-gradient-to-l from-[#F5F5F7] to-transparent z-10" />

        <div className="marquee-track flex items-center whitespace-nowrap gap-16 lg:gap-24 w-max">
          {items.map((area, i) => (
            <div
              key={`${area}-${i}`}
              className="flex items-center gap-16 lg:gap-24"
            >
              <span className="font-display font-semibold text-[#0A0A0A] text-4xl sm:text-6xl lg:text-7xl tracking-tight">
                {area}
              </span>
              <span className="w-2.5 h-2.5 rounded-full bg-[#1E63FF] shrink-0" />
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-12 lg:mt-16">
        <Reveal>
          <p className="text-sm text-neutral-500 max-w-xl">
            {t.areas.note}
          </p>
        </Reveal>

        <Reveal delay={100}>
          <div className="mt-10 lg:mt-14">
            <div className="overline text-neutral-500 mb-4">{t.areas.chipsTitle}</div>
            <ul className="flex flex-wrap gap-2 max-w-4xl">
              {SEO_PHRASES.map((p) => (
                <li
                  key={p}
                  className="text-[13px] font-medium text-[#0A0A0A] bg-white border border-black/10 rounded-full px-3.5 py-1.5 hover:border-[#1E63FF] hover:text-[#1E63FF] transition-colors"
                >
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
